import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    matchPath, useLocation
} from 'react-router-dom';

import {
    useSelectorIsOneRosterActivatedSchool,
    AppState, useSelectorAppDynamicParams, useSelectorCurrentClassroom, useSelectorCurrentProductId
} from '../../../app';
import { convertObjectToSearchParams } from '../../../app/services/hooks';
import {
    AppDynamicParams, CurrentClassroom
} from '../../../app/state/app.types';
import { ProductId } from '../../../product-selector';
import { useModuleByIdSelector } from '../../../user-profiles';
import { ROUTES } from '../../routes';
import {
    Country, User
} from '@api';

/**
 * This is the api contract between the iframe modules and the Portal itself,
 * currently this is passed through the iframe src search params.
 */
export type ContentModulePublicApi =
    & Partial<CurrentClassroom>
    & Partial<UserInfoData>
    & Partial<AppDynamicParams>
    & { productId?: ProductId };

export function useContentIframeSource (): string | null {
    const moduleId = useModuleParams();
    const module = useModuleByIdSelector(moduleId || 0);
    const productId = useSelectorCurrentProductId();
    const currentClassroom = useSelectorCurrentClassroom();
    const dynamicParams = useSelectorAppDynamicParams();
    const userInfo = useSelectorUserInfo();
    const isOneRosterActivatedSchool = useSelectorIsOneRosterActivatedSchool();
    const queryParams: ContentModulePublicApi = {
        productId,
        isOneRosterActivatedSchool,
        ...currentClassroom,
        ...userInfo,
        dynamicParams: JSON.stringify(dynamicParams)
    };

    if (module?.moduleLink.includes('productId')) delete queryParams['productId'];

    const params = convertObjectToSearchParams(queryParams);

    return useMemo(() => {
        // student view iframe link is incorrectly handling the classId and groupId
        // Since it doesn't require any of the params, so returning just the module link
        if (module?.moduleLink.includes('studentView')) {
            const locale = queryParams['locale'];

            return module.moduleLink + `&locale=${locale}`;
        }

        return module?.moduleLink + `&${params}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleId, module]);
}

type UserInfoData = {
    countryCode: string
    locale: string
    name: string
    username: string
    userProfileId: string
    regionId: number
};

function useSelectorUserInfo (): Partial<UserInfoData> {
    const user = useSelector<AppState, User | null>(state => state.userProfiles.data?.user || null);
    const country = useSelector<AppState, Country | null>(state => state.userProfiles.data?.country || null);
    // TODO discuss usage of fallback to en in this context, perhaps the iframe modules need to also
    // have the same fallback logic as this main app. Eg there maybe a culture code for the module but not the portal.
    const countryCode = country?.code;
    const locale = country?.cultureCode;
    const name = `${user?.firstName} ${user?.lastName}`;
    const username = user?.username;
    const userProfileId = user?.userProfileId;
    const regionId = user?.regionId;

    return {
        countryCode,
        locale,
        name,
        username,
        userProfileId,
        regionId
    };
}

export function useModuleParams (): number | null {
    const location = useLocation();
    const match = matchPath<{ moduleId: string }>(location.pathname, {
        path: ROUTES.contentByModuleId,
        exact: true
    });
    const moduleId = match?.params?.moduleId;

    if (moduleId && !isNaN(parseInt(moduleId))) {
        return parseInt(moduleId);
    }

    return null;
}
